import { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { AlertTriangle, CheckCircle2, Clock, Printer as PrinterIcon, Play, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

const ACTION_COLOR = {
  paused: 'bg-yellow-500/20 text-yellow-400',
  stopped: 'bg-red-500/20 text-red-400',
  alert: 'bg-orange-500/20 text-orange-400',
  ignored: 'bg-gray-700/50 text-gray-500',
};

const ACTION_LABEL = {
  paused: '자동 일시정지',
  stopped: '중단',
  alert: '알림만',
  ignored: '무시',
};

const PAGE_SIZE = 20;

export default function FailuresPage() {
  const [failures, setFailures] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadFailures = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const [f, s] = await Promise.all([
        api.get(`/failures?page=${p}&limit=${PAGE_SIZE}`),
        api.get('/failures/stats'),
      ]);
      setFailures(f.data.items || []);
      setTotal(f.data.total || 0);
      setStats(s.data);
    } catch (e) {
      console.error('실패 목록 조회 실패:', e);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadFailures(page);
  }, [page, loadFailures]);

  const handleClearAll = async () => {
    if (!confirm(`감지된 모든 실패 이력(${total}건)을 초기화하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) return;
    setClearing(true);
    try {
      const { data } = await api.delete('/failures');
      alert(`${data.deleted}건의 실패 이력이 초기화되었습니다.`);
      setPage(1);
      await loadFailures(1);
    } catch (e) {
      alert(e.response?.data?.message || '초기화 실패');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div>
      <PageHeader icon={AlertTriangle} title="AI 실패 감지">
        {total > 0 && (
          <button
            onClick={handleClearAll}
            disabled={clearing}
            className="flex items-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 border border-red-500/30 rounded-lg text-red-400 text-sm font-medium transition-colors"
          >
            <Trash2 size={14} />
            {clearing ? '초기화 중...' : '전체 초기화'}
          </button>
        )}
      </PageHeader>

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-orange-400" />
              <span className="text-gray-400 text-sm">주간 총 감지</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Play size={16} className="text-yellow-400" />
              <span className="text-gray-400 text-sm">자동 일시정지</span>
            </div>
            <p className="text-3xl font-bold text-yellow-400">{stats.pausedCount}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <PrinterIcon size={16} className="text-gray-500" />
              <span className="text-gray-400 text-sm">영향받은 프린터</span>
            </div>
            <p className="text-3xl font-bold text-white">{Object.keys(stats.byPrinter || {}).length}</p>
          </div>
        </div>
      )}

      {/* 실패 목록 (알림 스트림) */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-semibold text-sm">
            실패 감지 이력
            {total > 0 && <span className="text-gray-500 font-normal ml-2">총 {total}건</span>}
          </h2>
        </div>

        {loading && failures.length === 0 && (
          <p className="text-gray-500 text-sm text-center py-8">로딩 중...</p>
        )}

        {!loading && failures.length === 0 && (
          <div className="text-center py-8">
            <CheckCircle2 className="mx-auto text-green-400 mb-2" size={32} />
            <p className="text-gray-400 text-sm">감지된 실패가 없습니다</p>
          </div>
        )}

        <div className="space-y-2">
          {failures.map(f => (
            <div
              key={f.id}
              className="border border-gray-700 bg-gray-800 rounded-lg p-4"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${ACTION_COLOR[f.action] || 'bg-gray-700'}`}>
                      {ACTION_LABEL[f.action] || f.action}
                    </span>
                    <span className="text-white font-semibold text-sm">
                      {f.printer?.name || `Printer #${f.printerId}`}
                    </span>
                    {f.job && (
                      <span className="text-xs text-gray-400">
                        Job #{f.job.id} · {f.job.productName}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      신뢰도 {Math.round((f.confidence || 0) * 100)}%
                    </span>
                  </div>
                  {f.notes && (
                    <p className="text-xs text-gray-300 mt-1">{f.notes}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <Clock size={12} />
                    <span>{dayjs(f.detectedAt).format('YYYY-MM-DD HH:mm:ss')}</span>
                  </div>
                  {f.snapshotPath && (
                    <img
                      src={f.snapshotPath}
                      alt="snapshot"
                      className="mt-2 rounded max-w-xs max-h-32 object-cover border border-gray-700"
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 페이지네이션 */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-800">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-gray-400 text-sm font-mono px-3">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
